/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import { Button, Stack, Tooltip, Typography } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { DataGrid } from '@mui/x-data-grid';
import { DateTime } from 'luxon';
import {
  FILE_NAME_TIMESTAMP_PATTERN,
  YEAR_MONTH_DAY_PATTERN,
} from 'modules/common/constants/date-range';
import { withTheme } from '@emotion/react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { TABLE_HEADER_FONT_COLOR } from 'modules/common/constants/style';
import {
  selectAlertExcel,
  selectAlertLogData,
  selectAlertPdf,
  selectAlertPdfError,
} from '../selectors';
import { selectAuthUser, selectOrganizationId } from '../../../../../common/auth/selectors';
import NoDataDialog from '../../export-visual-data/components/no-data';
import { alertActions } from '../slice';
import ORDER_BY from '../../../../../common/constants/order-by';
import { selectHotelName, selectIsDemoModeActivated } from '../../../../selectors';
import { ALERT_TYPES, AUDIT_TABLE_PAGE_SIZE, notificationStatus } from '../constants';
import { exportSemanticLogToPdf, isSemanticType } from '../functions';
import { DrawerTableStyles } from '../style';
/**
 * Alert list handle by this component
 * @param {*} isShow - set show/hide state of side drawer
 * @param {*} setSelectedAlertId - selected alert id
 * @returns
 */
const LogTableView = ({ setNotificationId, setShowNotification, isDrawerOpen = false }) => {
  const dispatch = useDispatch();
  //
  // Log timestamps are historical UTC values from the DB. Format them in a
  // fixed UTC zone so the display never shifts with the viewer's timezone.
  const formatLogDate = (value, pattern) => {
    const dateTime = DateTime.fromISO(value, { zone: 'utc' });
    return dateTime.isValid ? dateTime.toFormat(pattern) : '';
  };
  //
  const auditLogData = useSelector(selectAlertLogData);
  const selectedHotel = useSelector(selectHotelName);
  const isDemoModeActivated = useSelector(selectIsDemoModeActivated);
  const organizationId = useSelector(selectOrganizationId);
  const authUser = useSelector(selectAuthUser);
  const alertExcel = useSelector(selectAlertExcel);
  const alertPdf = useSelector(selectAlertPdf);
  const alertPdfError = useSelector(selectAlertPdfError);
  // Holds the title/fileName/parameters for the in-flight semantic PDF export;
  // the remarks payload itself arrives on alertPdf once the saga succeeds.
  const pdfExportRef = useRef(null);
  const excelExportRef = useRef(null);
  //
  const [logAlerts, setLogAlerts] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [isDialog, setIsDialog] = useState(false);
  const [logPage, setLogPage] = useState(0); // current page
  const [logPageSize, setLogPageSize] = useState(AUDIT_TABLE_PAGE_SIZE); // rows per page
  const [logRowCount, setLogRowCount] = useState(0); // total number of log entries
  const [logSortDirection, setLogSortDirection] = useState(ORDER_BY.DESCENDING);

  // Fetch logs when scope, pagination or date ordering changes.
  useEffect(() => {
    const hotelQuery = selectedHotel ? `hotel[]=${encodeURIComponent(selectedHotel.name)}&` : '';
    //
    const queryString = `${hotelQuery}limit=${logPageSize}&page=${logPage}&sortBy=evalDate:${logSortDirection}`;
    //
    dispatch(
      alertActions.getAuditLogTableData({
        organizationId,
        userId: authUser?.id,
        query: queryString,
      })
    );
  }, [organizationId, authUser, logPage, logPageSize, selectedHotel, logSortDirection]);

  // define table column visibility modal
  const columnVisibilityModel = {
    id: false,
  };
  //
  const logColumns = [
    {
      field: 'id',
      headerName: 'ID',
      headerClassName: 'super-app-theme--header',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'createdAt',
      headerName: 'Generated Date',
      headerClassName: 'super-app-theme--header',
      flex: 1,
      minWidth: isDrawerOpen ? 130 : 200,
    },
    {
      field: 'alertName',
      headerName: 'Name',
      headerClassName: 'super-app-theme--header',
      flex: 1.5,
      minWidth: isDrawerOpen ? 150 : 200,
    },
    {
      field: 'type',
      headerName: 'Type / Description',
      headerClassName: 'super-app-theme--header',
      flex: 1,
      minWidth: isDrawerOpen ? 130 : 200,
    },
    {
      field: 'targetDate',
      headerName: 'Target Date or Period',
      headerClassName: 'super-app-theme--header',
      flex: 1,
      minWidth: isDrawerOpen ? 160 : 240,
    },
    {
      field: 'notificationStatus',
      headerName: 'Notification Status',
      headerClassName: 'super-app-theme--header',
      flex: 1,
      minWidth: isDrawerOpen ? 140 : 200,
    },
    {
      field: 'fileName',
      headerName: 'Attachments',
      align: 'center',
      headerAlign: 'center',
      flex: 0.5,
      minWidth: isDrawerOpen ? 95 : 140,
      sortable: false,
      renderCell: (params) => {
        const fileName = params.value;
        const { notificationId, alertId, summaryIndex, type, alertName, generatedAt } = params.row;
        const downloadFileName = [
          alertName || type,
          generatedAt ? formatLogDate(generatedAt, FILE_NAME_TIMESTAMP_PATTERN) : '',
        ]
          .filter(Boolean)
          .join('_')
          .replace(/[/\\:*?"<>|]/g, '-');
        //
        const handleDownload = async (event) => {
          event.stopPropagation();
          if (downloading) return;
          excelExportRef.current = downloadFileName;
          dispatch(
            alertActions.getAlertExcel({
              organizationId,
              notificationId,
              alertId,
              query: summaryIndex == null ? '' : `summaryIndex=${summaryIndex}`,
            })
          );
        };
        //
        const isSemantic = isSemanticType(type);
        //
        const handlePdfDownload = (event) => {
          event.stopPropagation();
          if (downloading) return;
          pdfExportRef.current = {
            title: type,
            fileName: downloadFileName,
          };
          setDownloading(true);
          dispatch(
            alertActions.getAlertPdf({
              organizationId,
              userId: authUser?.id,
              query: `alert_id=${alertId}&notification_id=${notificationId}${
                summaryIndex == null ? '' : `&summaryIndex=${summaryIndex}`
              }`,
            })
          );
        };
        //
        const isDisabled = !fileName || isDemoModeActivated || isSemantic;
        //
        if (isSemantic) {
          return (
            !isDemoModeActivated && (
              <Tooltip title={`${downloadFileName}.pdf`} arrow>
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    endIcon={<FileDownloadIcon fontSize="small" />}
                    onClick={handlePdfDownload}
                    disabled={downloading}
                    sx={{
                      borderRadius: '16px',
                      fontSize: '0.60rem',
                      padding: '2px 8px',
                      minWidth: 'auto',
                    }}
                  >
                    PDF
                  </Button>
                </span>
              </Tooltip>
            )
          );
        }
        //
        return (
          !isDisabled && (
            <Tooltip title={`${downloadFileName}.xlsx`} arrow>
              <span>
                <Button
                  variant="contained"
                  size="small"
                  endIcon={<FileDownloadIcon fontSize="small" />}
                  onClick={handleDownload}
                  sx={{
                    borderRadius: '16px',
                    fontSize: '0.60rem',
                    padding: '2px 8px',
                    minWidth: 'auto',
                  }}
                >
                  XLSX
                </Button>
              </span>
            </Tooltip>
          )
        );
      },
    },
  ];

  // Trigger download automatically when alertExcel is updated
  useEffect(() => {
    if (!alertExcel?.buffer || !alertExcel?.fileName || downloading) return;
    const downloadFile = () => {
      try {
        setDownloading(true);

        // Convert Node-style buffer to Uint8Array
        const uint8Array = new Uint8Array(alertExcel.buffer.data);

        // Create a Blob
        const blob = new Blob([uint8Array], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        // Create URL and trigger download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${excelExportRef.current || alertExcel.fileName}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Clear Redux store after download to free memory
        dispatch(alertActions.setAlertExcel(null));
      } catch (err) {
        setIsDialog(true);
      } finally {
        excelExportRef.current = null;
        setDownloading(false);
      }
    };

    downloadFile();
  }, [alertExcel, dispatch, downloading]);

  // Trigger PDF export automatically when semantic remarks are fetched
  useEffect(() => {
    if (!alertPdf || !pdfExportRef.current) return;
    try {
      exportSemanticLogToPdf({
        title: pdfExportRef.current.title,
        remarks: alertPdf,
        fileName: pdfExportRef.current.fileName,
      });
    } catch (err) {
      setIsDialog(true);
    } finally {
      pdfExportRef.current = null;
      setDownloading(false);
      // Clear Redux store after download to free memory
      dispatch(alertActions.setAlertPdf(null));
    }
  }, [alertPdf, dispatch]);

  // Surface semantic remarks fetch failures to the user
  useEffect(() => {
    if (!alertPdfError) return;
    pdfExportRef.current = null;
    setDownloading(false);
    setIsDialog(true);
    dispatch(alertActions.setAlertPdfError(null));
  }, [alertPdfError, dispatch]);
  //
  const logColumnsWithTooltip = logColumns.map((col) => ({
    ...col,
    // Other columns have no server ordering contract; avoid page-local sorts.
    sortable: col.field === 'createdAt',
    description:
      col.description ??
      (() => {
        switch (col.field) {
          case 'createdAt':
            return 'The date and time when the alert was executed and the results were generated. Dates/times are UTC.';
          case 'targetDate':
            return 'The defined stay date/period that was analyzed in this alert.';
          case 'notificationStatus':
            return 'Indicates whether the alert results were successfully sent to the configured notification channels.';
          default:
            return undefined;
        }
      })(),
    //
    renderHeader: (params) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {params.colDef.headerName}
        </Typography>

        {params.colDef.description && (
          <Tooltip title={params.colDef.description} arrow placement="top">
            <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'pointer' }} />
          </Tooltip>
        )}
      </div>
    ),
  }));
  //
  const formatNotificationStatus = (status) => {
    if (!status) return '';
    //
    if (status === notificationStatus.PENDING) return 'E-mail queued';
    if (status === notificationStatus.SUCCESS) return 'E-mail sent';
    if (status === notificationStatus.FAILED) return 'E-mail Failed';

    // Otherwise, capitalize only the first letter
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  // set formatted alert details
  useEffect(() => {
    // Details for the Log UI
    const formattedLogAlerts = auditLogData?.results?.map((alert) => ({
      id: alert?.rowId ?? alert?.id,
      notificationId: alert?.notificationId ?? alert?.id?.substring(0, alert.id.lastIndexOf('-')),
      alertId: alert?.alertId,
      summaryIndex: alert?.summaryIndex,
      createdAt: alert?.createdAt ? formatLogDate(alert.createdAt, YEAR_MONTH_DAY_PATTERN) : '',
      generatedAt: alert?.generatedAt,
      alertName: alert?.alertName,
      type: ALERT_TYPES[alert.alertProc].typeName,
      targetDate:
        alert?.targetDateStart && alert?.targetDateEnd
          ? `${formatLogDate(alert.targetDateStart, YEAR_MONTH_DAY_PATTERN)} - ${formatLogDate(
              alert.targetDateEnd,
              YEAR_MONTH_DAY_PATTERN
            )}`
          : '',
      notificationStatus: alert?.toNotify
        ? formatNotificationStatus(alert?.notificationStatus)
        : 'Notification Muted',
      fileName: alert?.fileName,
      remarks: alert?.remarks,
    }));
    setLogAlerts(formattedLogAlerts);
    setLogRowCount(auditLogData?.total || 0);
  }, [auditLogData]);
  //
  useEffect(() => {
    if (logRowCount === 0) {
      setShowNotification(false);
    }
  }, [logRowCount]);
  //
  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <Typography variant="h4">Logs</Typography>

        <Tooltip
          title="The date and time when the channel (e.g. email, SMS, etc.) was triggered. The evaluation results attachment can be downloaded as well."
          arrow
          placement="top"
        >
          <InfoOutlinedIcon
            sx={{
              fontSize: 18,
              color: 'text.secondary',
              cursor: 'pointer',
              mt: '2px',
            }}
          />
        </Tooltip>
      </Stack>

      <div style={{ width: '100%', minWidth: 0 }}>
        <DataGrid
          autoHeight
          sx={{
            '& .super-app-theme--header': {
              backgroundColor: 'white',
              color: TABLE_HEADER_FONT_COLOR,
            },
            '& .MuiDataGrid-main': {
              backgroundColor: 'white',
            },
            '& .MuiDataGrid-footerContainer': {
              backgroundColor: 'white',
            },
            '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '6px' },
            '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '10px' },
            '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: '20px' },
            ...(isDrawerOpen ? DrawerTableStyles : {}),
          }}
          columnVisibilityModel={columnVisibilityModel}
          pageSize={logPageSize}
          rowsPerPageOptions={[10, 15, 20]}
          rowHeight={50}
          rows={logAlerts ?? []}
          getRowHeight={() => 'auto'}
          columns={logColumnsWithTooltip}
          pagination
          paginationMode="server"
          sortingMode="server"
          sortingOrder={['desc', 'asc']}
          sortModel={[{ field: 'createdAt', sort: logSortDirection }]}
          onSortModelChange={(model) => {
            const direction = model[0]?.sort ?? ORDER_BY.DESCENDING;
            setLogSortDirection(direction);
            setLogPage(0);
          }}
          rowCount={logRowCount} // total rows from backend
          page={logPage}
          onPageChange={(newPage) => setLogPage(newPage)}
          onPageSizeChange={(newPageSize) => {
            setLogPageSize(newPageSize);
            setLogPage(0); // reset page when page size changes
          }}
          hideFooterSelectedRowCount
          onRowClick={(params) => {
            dispatch(alertActions.setAlertRemarks(null));
            const { notificationId, alertId, summaryIndex } = params.row;
            setShowNotification(true);
            setNotificationId(params.row);
            dispatch(
              alertActions.getAlertRemarks({
                organizationId,
                userId: authUser?.id,
                query: `alert_id=${alertId}&notification_id=${notificationId}${
                  summaryIndex == null ? '' : `&summaryIndex=${summaryIndex}`
                }`,
              })
            );
          }}
        />
      </div>
      <NoDataDialog
        open={isDialog}
        onClose={() => setIsDialog(false)}
        message="Something went wrong."
      />
    </>
  );
};
//
export default withTheme(LogTableView);
